import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { FormattedMessage } from "react-intl";

import CourseCard from "../../components/CourseCard";
import ProviderCard from "../../components/ProviderCard";
import PlacementCard from "../../components/PlacementCard";
import { Container } from "../../components";

// A grid lets a two-row skyscraper hold the last column while the results fill the cells around it
export const Wrapper = styled.ol`
  margin-top: 0.5rem;
  list-style: none;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  padding: 1rem 0;
  min-width: 100%;
  min-height: 300px;

  @media (${props => props.theme.tabletScreen}) {
    margin-top: 1rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (${props => props.theme.desktopScreen}) {
    height: auto;
    padding: 1rem;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (${props => props.theme.hugeScreen}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const Result = styled.li`
  margin: 0 0 1rem;
  position: relative;

  @media (${props => props.theme.tabletScreen}) {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  @media (${props => props.theme.desktopScreen}) {
    margin-bottom: 2rem;
  }
`;

const SkyscraperResult = styled(Result)`
  @media (${props => props.theme.tabletScreen}) {
    grid-column: -2 / -1;
    grid-row: 1 / span 2;
  }
`;

const MoreResults = styled.p`
  padding: 0.5rem;
  background-color: ${props => props.theme.white};
  box-shadow: ${props => props.theme.boxShadowLifted};
  border-radius: ${props => props.theme.borderRadius};
  max-width: 640px;
  text-align: center;
  margin: 0 auto 3rem;
  width: 90%;
  color: ${props => props.theme.info};
`;

const ResultsList = ({ results, onCategoryClick, hasMore, term, skyscraper }) => {
  if (results.length === 0) {
    return "";
  }

  const cardFor = result => {
    switch (result.modelName.toLowerCase()) {
      case "provider":
        return (
          <ProviderCard
            provider={result}
            listView={false}
            onCategoryClick={onCategoryClick}
            term={term}
          />
        );
      case "course":
        return (
          <CourseCard
            course={result}
            listView={false}
            onCategoryClick={onCategoryClick}
            provider={result.provider}
          />
        );
      case "placement":
        return (
          <PlacementCard placement={result} listView={false} onCategoryClick={onCategoryClick} />
        );
      default:
        return null; // Return nothing if the modelName doesn't match
    }
  };

  return (
    <Container>
      <Wrapper>
        {skyscraper && <SkyscraperResult>{skyscraper}</SkyscraperResult>}
        {results.map(result => (
          <Result key={`${result.modelName}-${result.id}`}>{cardFor(result)}</Result>
        ))}
      </Wrapper>
      {hasMore && (
        <MoreResults>
          <FormattedMessage id="pages.SearchmoreResults" />
        </MoreResults>
      )}
    </Container>
  );
};

ResultsList.propTypes = {
  results: PropTypes.array.isRequired,
  onCategoryClick: PropTypes.func.isRequired,
  hasMore: PropTypes.bool,
  term: PropTypes.string,
  skyscraper: PropTypes.node,
};

ResultsList.defaultProps = {
  hasMore: false,
  term: "",
  skyscraper: null,
};

export default ResultsList;
